// Displaying task-action ids compactly.
//
// A recorder mints ids as `<task-prefix>-<seq>` (taskActionIdPrefix in src/guidance/lib/task-actions-script.ts):
// the task prefix is identical for every action of one task, so within a task the seq alone identifies an
// action. That is what makes an id worth showing at all — a page listing one task's actions can label each
// row `#12` instead of repeating `act-9f21c04ab7de-1a2b3c4d-` on every line, and a dependency that DOES cross a task
// boundary keeps its full id, so the two are told apart by looking rather than by counting characters.

// The two halves of an action id, or null for an id that does not follow the scheme (a hand-written id, or
// the `noop-<n>` ids the disabled recorder hands out). Such ids are displayed verbatim, never mangled.
export function parseActionId(id: string): { prefix: string; seq: number } | null {
  const m = /^(.*)-(\d+)$/.exec(id)
  if (!m || !m[1]) return null
  return { prefix: m[1], seq: Number(m[2]) }
}

/**
 * The id to SHOW for an action, given the action it is being shown next to (`relativeTo` — a row's own id
 * when labelling its dependencies, or any row of the list when labelling the rows themselves).
 *
 * Same task → `#<seq>`. Different task, or an unparseable id, or no reference → the full id, because the
 * only compact form available would collide with the seqs of the task being read.
 */
export function shortActionId(id: string, relativeTo?: string | null): string {
  const self = parseActionId(id)
  if (!self) return id
  if (!relativeTo) return id
  const other = parseActionId(relativeTo)
  if (!other || other.prefix !== self.prefix) return id
  return `#${self.seq}`
}
